import { Component, OnInit, Input, OnDestroy, ViewChild } from '@angular/core';
import { LookupService } from '@services/lookup.service';
import { SearchStrategyService } from '@services/search-strategy.service';
import { TreeViewService } from '@services/treeview.service';
import { LocationCriteria } from './filter-location.model';
import { Country } from '@models/country.model';
import { List } from 'linqts-es-module';
import { Region } from '@models/region.model';
import { RegionCountry } from '@models/regioncountry.model';
import { TreeNode } from 'primeng/api';
import { Subject } from 'rxjs';
import { FilterUtilService } from '../shared/filter-util.service';
import { ControlContainer, NgForm } from '@angular/forms';
import { WorldRegionsComponent } from '@shared/world-regions/world-regions.component';
import { AddressType } from './address-type.model';
import { AppState } from '../../../reducers';
import { select, Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { isTradingAddressAvailable } from '../../analysis/search-strategy/state/study-search-strategy.selectors';

@Component({
  selector: 'app-filter-location',
  templateUrl: './filter-location.component.html',
  styleUrls: ['./filter-location.component.css'],
  providers: [SearchStrategyService],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class FilterLocationComponent implements OnInit, OnDestroy {
  @Input() filter: any;
  @Input() submitClicked: boolean;
  @Input() readOnly = false;

  @ViewChild(WorldRegionsComponent, { static: false })
  worldRegionsComponent: WorldRegionsComponent;

  selectedCountryNodes: TreeNode[] = [];
  regionCountries_multi: TreeNode[] = [];
  formDirty = false;
  filterCountryOptions: Country[];
  region: List<Region> = new List<Region>();
  lstSortTree: List<TreeNode> = new List<TreeNode>();
  regionCountryList: List<RegionCountry> = new List<RegionCountry>();
  countryId: number;
  dataLoaded = false;
  isAddressTypeVisible = false;
  incorporationAddressType: number = AddressType.IncorporationAddress;
  tradingAddressType: number = AddressType.TradingAddress;

  destroy$ = new Subject();

  constructor(
    private lookupService: LookupService,
    private treeviewService: TreeViewService,
    private filterUtilService: FilterUtilService,
    private store: Store<AppState>
  ) {}

  ngOnInit() {
    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(isTradingAddressAvailable))
      .subscribe((isAvailable) => {
        this.udpateAddressTypeRelatedData(isAvailable);
      });

    this.loadInitialData();
  }

  initializeForm() {
    const filter = <LocationCriteria>this.filter;
    if (filter.locationSelection) {
      this.setSelectedCountryData(filter);
    }
  }

  loadInitialData() {
    this.filterUtilService.spinnerRS.next(true);
    this.dataLoaded = false;

    this.regionCountries_multi = [];
    this.filterCountryOptions = [];
    this.regionCountryList = new List<RegionCountry>();

    this.lookupService.GetAllRegionCountries$().subscribe((_regionCountry) => {
      this.regionCountryList.AddRange(_regionCountry);
      this.regionCountries_multi = this.getMultiSelectRegionCountry();
      this.initializeForm();
      this.dataLoaded = true;
      this.filterUtilService.spinnerRS.next(false);
    });
  }

  private getMultiSelectRegionCountry() {
    const regionDetails = this.regionCountryList
      .Select((a) => a.region)
      .Distinct()
      .OrderBy((a) => a.regionName.toUpperCase());

    // display Global region first
    const globalRegion = regionDetails.Where((r) => r.regionName == 'Global').FirstOrDefault();
    regionDetails.Remove(globalRegion);
    regionDetails.Insert(0, globalRegion);

    return regionDetails
      .GroupJoin(
        this.regionCountryList,
        (ctry) => ctry.regionId,
        (rg) => rg.regionId,
        (ctry, rg) => ({
          label: ctry.regionName,
          data: '0',
          leaf: false,
          children: rg.Select((a) => ({ label: a.country.countryName, data: a.countryId.toString(), leaf: true })).ToArray(),
        })
      )
      .ToArray() as TreeNode[];
  }

  nodeSelect() {
    const filter = <LocationCriteria>this.filter;
    this.setSelectedCountryData(filter);
    this.sortSelection(filter.locationSelection);
  }

  nodeUnselect(event, emitChildEvent = false) {
    const filter = <LocationCriteria>this.filter;

    if (emitChildEvent) {
      this.worldRegionsComponent.nodeUnselect(event, true);
    }

    if (event.node.data.toString() === '0' && !event.node.leaf) {
      event.node.children.forEach((val) => {
        let index = -1;
        if (val.data.toString() === '0') {
          val.children.forEach((vald) => {
            index = filter.locationSelection.findIndex((a) => a.data === vald.data);
            if (index !== -1) {
              filter.locationSelection.splice(index, 1);
            }
          });
        } else {
          index = filter.locationSelection.findIndex((a) => a.data === val.data);
          if (index !== -1) {
            filter.locationSelection.splice(index, 1);
          }
        }
      });
    } else {
      const index = filter.locationSelection.findIndex((a) => a.data === event.node.data);
      if (index !== -1) {
        filter.locationSelection.splice(index, 1);
      }
    }

    this.arrangeRegionCountryData(filter);
  }

  private arrangeRegionCountryData(filter: LocationCriteria) {
    this.lstSortTree = new List<TreeNode>();
    this.lstSortTree.AddRange(filter.locationSelection);
    this.lstSortTree.OrderBy((a) => a.label);
    filter.locationSelection = [];

    this.lstSortTree.ForEach((val) => {
      val.parent = null;
    });
    filter.locationSelection = this.lstSortTree.ToArray();
  }

  private setSelectedCountryData(filter: LocationCriteria) {
    this.selectedCountryNodes = [];
    this.treeviewService.checkNode(this.regionCountries_multi, filter.locationSelectionId, this.selectedCountryNodes);
    this.selectedCountryNodes.forEach((val) => {
      if (val.data.toString() !== '0') {
        if (!filter.locationSelection || filter.locationSelection.filter((a) => a.data === val.data).length === 0) {
          filter.locationSelection.push(val);
        }
      }
    });

    this.setSelectablePropertyToLocationSelection(filter);

    this.arrangeRegionCountryData(filter);
  }

  private setSelectablePropertyToLocationSelection(filter: LocationCriteria) {
    filter.locationSelection.forEach((val) => {
      val.selectable = !this.readOnly;
    });
  }

  public sortSelection(selection: TreeNode[]): void {
    return this.filterUtilService.sortSelection(selection);
  }

  onWorldRegionsClick() {
    this.formDirty = true;
  }

  udpateAddressTypeRelatedData(isAvailable: boolean): void {
    if (isAvailable) {
      if (this.filter.addressType === null) {
        this.filter.addressType = AddressType.IncorporationAddress;
      }
      this.isAddressTypeVisible = true;
    } else {
      this.filter.addressType = null;
      this.isAddressTypeVisible = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next('');
  }
}
